import React, { useRef, useState } from 'react'

import DisplayResult from './display-result'
import './excel-drop-area.css'
import {
  ExcelDropAreaProps,
  ExcelDropAreaResult,
} from './excel-drop-area.types'
import FileDetailIcon from './file-detail-icon'

/**
 *
 * The ExcelDropArea has the following assumptions and limitations:
 *
 * - It only accepts one file at a time
 * - It only accepts files with extension ".xlsx"
 * - It expects a processFileHandler function to be provided which will be
 *   executed to handle the selected file.  This function should return
 *   an ExcelDropAreaResult which will be used to display status and
 *   messages to the user.
 */
const ExcelDropArea = (props: ExcelDropAreaProps) => {
  const inputFileRef = useRef<HTMLInputElement | null>(null)

  const [dragging, setDragging] = useState(false)
  const [file, setFile] = useState<File | null>()

  const [result, setResult] = useState<ExcelDropAreaResult | null>(null)
  const [loading, setLoading] = useState(false)

  const handleDragEnter = (e) => {
    e.preventDefault()
    setDragging(true)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    setDragging(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()

    // If the user drags over a child component of the drop area, a drag leave
    // event will be generated.  Ignore such spurious leave events so that
    // we don't generate a leave followed by an enter when dragging across a
    // child.
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragging(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragging(false)
    reset()
    const file = e.dataTransfer.files[0]
    checkFileType(file)
    setFile(file)
  }

  const handleClick = () => {
    inputFileRef.current?.click()
  }

  const handleFileUpload = (e) => {
    reset()
    const { files } = e.target
    if (files && files.length) {
      const file = files[0]
      checkFileType(file)
      setFile(file)
    }
  }

  const checkFileType = (file: File) => {
    const filename = file.name
    const parts = filename?.split('.')
    const fileType = parts?.[parts?.length - 1]
    if (fileType !== 'xlsx') {
      setResult({
        isError: true,
        message: 'Please drop an Excel file with extension \'.xlsx\'',
      })
    }
  }

  const handlePlayClicked = async () => {
    setLoading(true)

    props.processFileHandler(file).then((result) => {
      setResult(result)
      setLoading(false)
    })
  }

  const reset = () => {
    setFile(null)
    setResult(null)
    setLoading(false)
  }

  return (
    <>
      <div
        className={`file-dropzone ${dragging ? 'dragging' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <div id='drop-text'>
          <p>{props.title}</p>
        </div>

        <div id='divide-upload'>
          <hr className='divide-upload-section' id='solid-divider' />
          <p className='divide-upload-section' id='divider-text'>
            OR
          </p>
          <hr className='divide-upload-section' id='solid-divider' />
        </div>

        <div id='file-click' onClick={handleClick}>
          <div id='file-click-button'>
            <p>Click to upload</p>
          </div>
        </div>

        <input
          accept='.xlsx'
          onChange={handleFileUpload}
          ref={inputFileRef}
          style={{ display: 'none' }}
          type='file'
        />
      </div>

      <div className='parsed-file-section'>
        {file && (
          <>
            <div className='fileDetailSection'>
              <div id='fileDetail'>
                <FileDetailIcon
                  loading={loading}
                  playClickedFunction={handlePlayClicked}
                  result={result!}
                />
                <div id='fileExtraInfo'>
                  <div className='extraSpace' id='fileName'>
                    <p>{file.name}</p>
                  </div>
                </div>
              </div>
            </div>
            <DisplayResult result={result!} />
          </>
        )}
      </div>
    </>
  )
}

export default ExcelDropArea
